// src/plugins/sentryInitializer.ts
import * as SentryVue from "@sentry/vue";
import * as SentryCapacitor from "@sentry/capacitor";
import type { App } from 'vue';
import type { Router } from 'vue-router';

interface InitOptions {
  app: App;
  router: Router;
  appVersion: string;
  // Filtering dimension in Sentry: web | electron | ios | android
  environment: string;
  // Separates per-platform source maps within the shared release: web | electron | mobile.
  // Runtime dist MUST match the dist used when uploading source maps for that platform.
  dist: string;
  native: boolean;
}

export function initializeSentry({
  app,
  router,
  appVersion,
  environment,
  dist,
  native,
}: InitOptions) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // No DSN configured (e.g. a misbuilt env) — skip rather than spin up a no-op
    // client, and make the misconfiguration explicit. Best-effort: only logs.
    console.warn("[sentry] VITE_SENTRY_DSN is empty; skipping initialization.");
    return;
  }
  // Must stay identical to the release name set on every source-map upload
  // (vite.config.ts / electron.vite.config.ts) or stacktraces won't symbolicate.
  const release = `hamrs@${appVersion}`;

  // Errors to drop before they ever leave the client.
  //
  // - latLngToLayerPoint: a benign Leaflet zoom-animation/tooltip teardown race
  //   (HAMRS-K). When an animated zoom's transitionend fires, Leaflet re-fires
  //   'zoom' and a permanent call-sign tooltip whose _map was already detached
  //   reads `this._map.latLngToLayerPoint(...)` -> null. It's thrown async from a
  //   DOM event (uncaught, unpreventable in our code), the zoom still completes,
  //   and the user sees nothing -- but a single user zooming spams dozens of
  //   events. The Leaflet method name is a stable, distinctive token that won't
  //   match unrelated errors. Matched here (not just in Sentry's UI) so it never
  //   counts against quota or fires alerts.
  const ignoreErrors: (string | RegExp)[] = [/latLngToLayerPoint/];

  if (native) {
    // @sentry/capacitor wires the native transport and delegates JS error
    // handling to the @sentry/vue sibling SDK (passed as the second arg). This
    // init path is what avoids the WKWebView crash that the raw @sentry/vue
    // browser integrations triggered (`new Proxy()` on a non-object target
    // inside the webview), which is why native was previously skipped entirely.
    SentryCapacitor.init(
      {
        app,
        dsn,
        release,
        dist,
        environment,
        ignoreErrors,
        integrations: [SentryVue.browserTracingIntegration({ router })],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ["localhost", "https://logger.hamrs.app"],
        // iOS App Hang Tracking (Cocoa SDK) defaults to a 2s threshold, which
        // false-positives on iOS's own keyboard input-mode loading and similar
        // system work the app doesn't control (HAMRS-1G). Raise it so we still
        // catch genuinely bad main-thread freezes (e.g. a sync PouchDB op) but
        // drop the routine ~2s system stalls.
        //
        // Requires @sentry/capacitor >= 1.5.0: only then does FilterNativeOptions
        // forward appHangTimeoutInterval to the native iOS SDK (see the SDK's
        // nativeOptions.ts -> iOSParameters). On Capacitor the native Cocoa SDK
        // is initialized through this JS bridge -- there is no separate native
        // init -- so this option IS the native config path. A downgrade below
        // 1.5.0 would silently drop it.
        appHangTimeoutInterval: 3,
      },
      SentryVue.init
    );
    return;
  }

  SentryVue.init({
    app,
    dsn,
    release,
    dist,
    environment,
    ignoreErrors,
    integrations: [
      SentryVue.browserTracingIntegration({ router }),
      SentryVue.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        unmask: [".sentry-unmask"],
        networkDetailAllowUrls: [window.location.origin, 'https://api.hamdb.org', 'https://api.hamrs.app', 'https://xmldata.qrz.com', 'https://hamqth.com'],
        networkRequestHeaders: ['X-Custom-Header'],
        networkResponseHeaders: ['X-Custom-Header'],
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", "https://logger.hamrs.app"],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
